import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Tag, Trash2, ShoppingCart, ShieldCheck, Loader2, CalendarDays, Lock } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { api, mediaUrl, money, errMsg } from "@/lib/api";
import PercentSelector from "@/components/site/PercentSelector";
import { Counter } from "@/pages/TourDetail";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

const today = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();

export default function CartPage() {
  const { cart, removeFromCart, updateCartItem, settings } = useSite();
  const options = settings?.deposit_options || [30, 50, 100];
  const [pct, setPct] = useState(() => {
    const v = Number(localStorage.getItem("rdx_percent")) || 30;
    return v;
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", hotel: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const percent = options.includes(pct) ? pct : options[0];

  const subtotal = cart.reduce((s, i) => s + i.price * i.adults + (i.child_price || 0) * i.children, 0);
  const [couponInput, setCouponInput] = useState(localStorage.getItem("rdx_coupon") || "");
  const [coupon, setCoupon] = useState(null); // {code, discount, description}
  const [couponLoading, setCouponLoading] = useState(false);
  const cartKey = cart.map((i) => `${i.tour_id}:${i.adults}:${i.children}`).join("|");

  const applyCoupon = async (code, silent = false) => {
    if (!code?.trim() || !cart.length) return;
    setCouponLoading(true);
    try {
      const { data } = await api.post("/coupons/validate", {
        code, items: cart.map((i) => ({ tour_id: i.tour_id, date: i.date, adults: i.adults, children: i.children })),
      });
      setCoupon(data); setCouponInput(data.code); localStorage.setItem("rdx_coupon", data.code);
      if (!silent) toast.success(`Cupón ${data.code} aplicado: -${money(data.discount)}`);
    } catch (err) {
      setCoupon(null);
      if (!silent) toast.error(errMsg(err, "Cupón no válido"));
      else localStorage.removeItem("rdx_coupon");
    } finally { setCouponLoading(false); }
  };
  const removeCoupon = () => { setCoupon(null); setCouponInput(""); localStorage.removeItem("rdx_coupon"); };

  // Re-validate coupon whenever the cart changes
  useEffect(() => {
    const c = coupon?.code || localStorage.getItem("rdx_coupon");
    if (c) applyCoupon(c, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartKey]);

  const discount = coupon ? Math.min(coupon.discount, subtotal) : 0;
  const total = subtotal - discount;
  const payNow = (total * percent) / 100;

  const checkout = async (e) => {
    e.preventDefault();
    if (!cart.length) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: cart.map((i) => ({ tour_id: i.tour_id, date: i.date, adults: i.adults, children: i.children })),
        customer: form, payment_percent: percent, origin_url: window.location.origin, coupon_code: coupon?.code || null,
      });
      localStorage.setItem("rdx_percent", String(percent));
      localStorage.removeItem("rdx_coupon");
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err, "No se pudo iniciar el pago"));
      setLoading(false);
    }
  };

  const inputCls = "w-full h-11 rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 transition-[border-color,box-shadow]";

  if (!cart.length) {
    return (
      <div className="py-28 text-center bg-gray-50" data-testid="cart-empty">
        <ShoppingCart className="w-14 h-14 mx-auto text-gray-300" />
        <h1 className="text-2xl font-bold text-gray-900 mt-4">Tu carrito está vacío</h1>
        <p className="text-gray-500 mt-2">Elige tu próxima aventura en República Dominicana.</p>
        <Link to="/excursiones" className="btn-accent inline-block mt-6 px-6 py-3 rounded-md font-semibold">Ver Excursiones</Link>
      </div>
    );
  }

  return (
    <div className="bg-gray-50" data-testid="cart-page">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Finalizar pago</h1>
        <form onSubmit={checkout} className="grid lg:grid-cols-[1fr_400px] gap-8">
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-lg mb-4">Tus excursiones</h2>
              <div className="divide-y divide-gray-100">
                {cart.map((i) => (
                  <div key={i.key} className="py-4 flex flex-col sm:flex-row gap-4" data-testid="cart-item">
                    <img src={mediaUrl(i.image)} alt={i.title} className="w-full sm:w-32 h-28 sm:h-24 object-cover rounded-md" />
                    <div className="flex-1">
                      <div className="flex justify-between gap-3">
                        <h3 className="font-semibold text-gray-900">{i.title}</h3>
                        <button type="button" onClick={() => removeFromCart(i.key)} aria-label="Eliminar" data-testid="cart-remove-btn" className="text-gray-400 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button type="button" className="flex items-center gap-1.5 text-sm text-gray-600 mt-1 hover:text-[var(--brand)]" data-testid="cart-date-btn">
                            <CalendarDays className="w-4 h-4" />{format(parseISO(i.date), "EEE d MMM yyyy", { locale: es })}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" locale={es} selected={parseISO(i.date)} disabled={(d) => d < today}
                            onSelect={(d) => d && updateCartItem(i.key, { date: format(d, "yyyy-MM-dd") })} initialFocus />
                        </PopoverContent>
                      </Popover>
                      <div className="grid sm:grid-cols-2 gap-x-6">
                        <Counter label="Adultos" sub={money(i.price)} value={i.adults} min={1} onChange={(v) => updateCartItem(i.key, { adults: v })} testid="cart-adults" />
                        {i.allow_children === false ? <div className="py-2 text-sm text-gray-500">Solo adultos (18+)</div> :
                          <Counter label="Niños" sub={money(i.child_price)} value={i.children} onChange={(v) => updateCartItem(i.key, { children: v })} testid="cart-children" />}
                      </div>
                      <div className="text-right font-semibold brand-text">{money(i.price * i.adults + (i.child_price || 0) * i.children)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-lg mb-4">Datos del cliente</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-sm font-medium text-gray-700">Nombre completo *</label><input required className={`${inputCls} mt-1.5`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checkout-name-input" /></div>
                <div><label className="text-sm font-medium text-gray-700">Correo electrónico *</label><input required type="email" className={`${inputCls} mt-1.5`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="checkout-email-input" /></div>
                <div><label className="text-sm font-medium text-gray-700">Teléfono / WhatsApp *</label><input required minLength={5} className={`${inputCls} mt-1.5`} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="checkout-phone-input" /></div>
                <div><label className="text-sm font-medium text-gray-700">Hotel de recogida</label><input className={`${inputCls} mt-1.5`} value={form.hotel} onChange={(e) => setForm({ ...form, hotel: e.target.value })} data-testid="checkout-hotel-input" /></div>
                <div className="sm:col-span-2"><label className="text-sm font-medium text-gray-700">Notas (opcional)</label><textarea rows={3} className={`${inputCls} h-auto py-2 mt-1.5 resize-none`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="checkout-notes-input" /></div>
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-[140px] h-fit bg-white rounded-xl shadow-md p-6" data-testid="checkout-summary">
            <h2 className="font-semibold text-lg">Resumen de pago</h2>
            <div className="mt-4" data-testid="coupon-box">
              <div className="text-sm font-medium text-gray-800 mb-2 flex items-center gap-1.5"><Tag className="w-4 h-4 brand-text" />¿Tienes un cupón de descuento?</div>
              {coupon ? (
                <div className="flex items-center justify-between rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50 px-3 py-2">
                  <div><div className="font-bold text-emerald-700 tracking-wider" data-testid="applied-coupon-code">{coupon.code}</div>
                    <div className="text-xs text-emerald-700">{coupon.description || (coupon.type === "percent" ? `${coupon.value}% de descuento` : `${money(coupon.value)} de descuento`)}</div></div>
                  <button type="button" onClick={removeCoupon} className="text-xs text-gray-500 hover:text-red-500 underline" data-testid="remove-coupon-btn">Quitar</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="CÓDIGO"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(couponInput); } }}
                    className={`${inputCls} uppercase tracking-wider`} data-testid="coupon-input" />
                  <button type="button" onClick={() => applyCoupon(couponInput)} disabled={couponLoading || !couponInput.trim()} data-testid="apply-coupon-btn"
                    className="btn-brand px-4 rounded-md text-sm font-semibold shrink-0 disabled:opacity-50 flex items-center gap-1.5">
                    {couponLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}Aplicar
                  </button>
                </div>
              )}
            </div>
            <div className="mt-5">
              <div className="text-sm font-medium text-gray-800 mb-2">Elige cuánto pagar ahora</div>
              <PercentSelector options={options} value={percent} onChange={setPct} total={total} />
            </div>
            <div className="mt-5 space-y-2 text-sm">
              <div className="flex justify-between text-gray-600"><span>Subtotal ({cart.length} excursión{cart.length > 1 ? "es" : ""})</span><span data-testid="checkout-subtotal">{money(subtotal)}</span></div>
              {coupon && <div className="flex justify-between text-emerald-600 font-medium"><span>Cupón {coupon.code}</span><span data-testid="checkout-discount">-{money(discount)}</span></div>}
              <div className="flex justify-between text-gray-900 font-medium"><span>Total</span><span data-testid="checkout-total">{money(total)}</span></div>
              <div className="flex justify-between text-gray-500"><span>Saldo pendiente (día del tour)</span><span data-testid="checkout-balance">{money(total - payNow)}</span></div>
              <div className="border-t border-gray-100 pt-3 flex justify-between text-lg font-bold text-gray-900"><span>A pagar hoy</span><span className="brand-text" data-testid="checkout-pay-now">{money(payNow)}</span></div>
            </div>
            <button disabled={loading} data-testid="checkout-submit-btn" className="btn-accent w-full h-12 rounded-md font-semibold mt-6 flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}Pagar {money(payNow)} con Stripe
            </button>
            <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500 mt-4"><ShieldCheck className="w-4 h-4 text-emerald-600" />Serás redirigido al pago seguro de Stripe</p>
          </aside>
        </form>
      </div>
    </div>
  );
}
